import React from 'react';
import { motion } from 'framer-motion';

/**
 * Shared dark hero used across the marketing sub-pages so they match the
 * modern site (black background, white headline, red accent eyebrow).
 * Content/copy is passed in by each page — this component only handles layout.
 */
const PageHero = ({ eyebrow, title, subtitle, children, align = 'center' }) => {
  const isCenter = align === 'center';

  return (
    <section className="relative isolate overflow-hidden bg-black pt-32 pb-20 md:pt-40 md:pb-28">
      {/* Ambient brand glow + subtle grid */}
      <div className="pointer-events-none absolute -top-1/3 left-1/2 h-[80vw] max-h-[820px] w-[80vw] max-w-[820px] -translate-x-1/2 rounded-full bg-red-600/10 blur-[120px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
          backgroundSize: '42px 42px',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`${isCenter ? 'mx-auto text-center' : ''} max-w-4xl`}>
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-red-500 sm:text-sm"
            >
              {eyebrow}
            </motion.p>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className={`mt-6 text-lg leading-relaxed text-gray-400 md:text-xl ${isCenter ? 'mx-auto' : ''} max-w-2xl`}
            >
              {subtitle}
            </motion.p>
          )}

          {children && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-10"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>

      {/* Fade into the next (black) section */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default PageHero;
